import { InjectionToken } from '@angular/core';

export interface PreviewMetadataFile {
  path: string;
  sha256: string;
  bytes: number;
  mediaType: string;
}

export interface PreviewMetadataDocument {
  courseId: string;
  packageFile: string;
  sourceFile: string;
  blockIds: string[];
  blocks: Array<{
    id: string;
    runtimeId: string;
    sourceLine?: number | null;
    type?: string;
  }>;
}

export interface PreviewMetadataDiagnostic {
  code: string;
  severity: 'error';
  courseId: string;
  sourceFile: string;
  line: number | null;
  blockType: string;
  lessonBlockId: string | null;
  message: string;
  hint: string | null;
  element?: {
    kind: string;
    index: number;
    id?: string | null;
  } | null;
}

export interface PreviewMetadataChangedDocument {
  courseId: string;
  moduleId: string;
  lessonId: string;
  sourceFile: string;
  reasons: Array<'content' | 'asset'>;
  assetFiles: string[];
}

export interface PreviewMetadataUnassignedDocument {
  courseId: string | null;
  sourceFile: string;
  message: string;
  line: number | null;
}

export interface PreviewMetadata {
  schemaVersion: 1;
  bundleId: string;
  bundleSha: string;
  generatedAt: string;
  appVersion: string;
  engineVersion: string;
  digest: string;
  files: PreviewMetadataFile[];
  documents: PreviewMetadataDocument[];
  diagnostics?: PreviewMetadataDiagnostic[];
  validationHints: string[];
  repository?: string;
  contentCommitSha?: string;
  baseCommitSha?: string;
  changes?: {
    documents: PreviewMetadataChangedDocument[];
    unassignedDocuments: PreviewMetadataUnassignedDocument[];
  };
}

export interface ContentPackageResponse<T> {
  ok: boolean;
  status: number;
  value: T | null;
}

export interface ContentPackageSource {
  readonly packageId: string;
  readJson<T>(relativePath: string): Promise<ContentPackageResponse<T>>;
  assetBaseUrl(manifestPath: string): string;
  loadPreviewMetadata(): Promise<PreviewMetadata | null>;
}

export class HttpContentPackageSource implements ContentPackageSource {
  readonly packageId: string;

  constructor(private readonly packageRoot: string, packageId = 'snapshot') {
    this.packageId = packageId;
    assertSafePackageRoot(packageRoot);
  }

  async readJson<T>(relativePath: string): Promise<ContentPackageResponse<T>> {
    const response = await fetch(this.resolveUrl(relativePath));
    return {
      ok: response.ok,
      status: response.status,
      value: response.ok ? await response.json() as T : null,
    };
  }

  assetBaseUrl(manifestPath: string): string {
    const normalized = normalizeRelativePackagePath(manifestPath);
    const directory = normalized.includes('/')
      ? normalized.slice(0, normalized.lastIndexOf('/') + 1)
      : '';
    return `/${this.packageRoot}/${directory}`;
  }

  loadPreviewMetadata(): Promise<PreviewMetadata | null> {
    return Promise.resolve(null);
  }

  private resolveUrl(relativePath: string): string {
    return `${this.packageRoot}/${normalizeRelativePackagePath(relativePath)}`;
  }
}

export function normalizeRelativePackagePath(relativePath: string): string {
  const normalized = relativePath.trim().replace(/^\.\//, '');
  if (
    !normalized
    || normalized.startsWith('/')
    || normalized.includes('\\')
    || normalized.includes('://')
    || normalized.split('/').some((segment) => segment === '..' || segment === '')
  ) {
    throw new Error(`Ungültiger Package-Pfad "${relativePath}".`);
  }
  return normalized;
}

function assertSafePackageRoot(packageRoot: string): void {
  const normalized = normalizeRelativePackagePath(packageRoot);
  if (normalized !== packageRoot || packageRoot.endsWith('/')) {
    throw new Error(`Ungültiger Package-Root "${packageRoot}".`);
  }
}

export const CONTENT_PACKAGE_SOURCE = new InjectionToken<ContentPackageSource>(
  'CONTENT_PACKAGE_SOURCE',
  { factory: () => new HttpContentPackageSource('content-packages/current') },
);
